import React, { useState } from 'react';
import { useTextResources } from '../../../language';
import { DatePicker } from '../../shared/DatePicker';
import { Form, Icon } from 'semantic-ui-react';
import DropDown from '../../shared/dropdown';
import HotelsDropDown from '../../shared/HotelsDropDown';
import CustomerDropDown from '../../shared/CustomerDropDown';
import { Reports } from '../../../api/services/next';
import moment from 'moment';
import { onError } from '../../shared/messages';
import { GetReservationTotals } from '../../../api/services/pms';
import { endpoint } from '../../../api/http';
import GridSearchTemplate from '../../../pages/GridSearchTemplate';
import { useLoader } from '../../../hooks/useLoader';
import _ from 'lodash'
import XLSX from "xlsx-js-style"
import { dateFormat, HotelFeatureCodes } from '../../shared/constants';
import RoomTypesDropDown from '../../shared/RoomTypesDropDown';
import { Popup } from 'semantic-ui-react';
import { createExcelHeader } from '../../GridPanel/utility';
import { useCurrentHotel } from '../../../hooks/useCurrentHotel';

export const CustomerIncomeReport = () => {
    const text = useTextResources();
    const [data, setData] = useState([]);
    const [fromDate, setFromDate] = useState();
    const [toDate, setToDate] = useState();
    const [hotelIds, setHotelIds] = useState([]);
    const [hotelHasCommissionFeature] = useState(window.optima?.user?.ChainHotels?.some(h => h.HotelFeatures.some(f => f.FeatureID === HotelFeatureCodes.CustomerCommissions)));
    const currentHotel = useCurrentHotel();
    const loader = useLoader()

    const dateTypes = [
        {
            text: text("CheckInDate"),
            value: "CheckInDate"
        },
        {
            text: text("CheckOutDate"),
            value: "CheckOutDate"
        },
        {
            text: text("CreateDate"),
            value: "CreateDate"
        },
    ]

    const formatter = ({ value }) => {
        return value || ""
    }

    const dateFormatter = ({ value }) => {
        return value && moment(value).format(dateFormat) || "";
    }

    const currencyFormatter = ({ value }) => {
        if (value)
            return `${value.toFixed(2)}`
        else
            return 0
    }

    const reservationFormatter = ({ value, row }) => {
        return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}PMS/Reservations/Index/${row.ReservationId}?hotelid=${row.HotelId}`}>{value}</a>
    }

    const customerFormatter = ({ value, row }) => {
        return row ? <a target="_blank" rel="noopener noreferrer" href={`${endpoint}CRM/Customers/Edit/${row?.CustomerId}`}>{value}</a> : value || "";
    }

    const excelHeaderTitle = `${text('CustomerCommission')} ${text("Dates")}: ${new Date(fromDate).toLocaleDateString()} - ${new Date(toDate).toLocaleDateString()}`;

    const getHotelsList = (forHead) => {
        let hotelsSet = [];
        for (let item of data)
            if (hotelsSet.indexOf(item.HotelName) === -1) {
                hotelsSet.push(item.HotelName);
                if (forHead) {
                    hotelsSet.push(item.HotelName);
                    hotelsSet.push(item.HotelName);
                }
            }
        return hotelsSet;
    }

    const getCustomersList = () => {
        let customersSet = [];
        for (let item of data)
            if (customersSet.indexOf(item.CustomerName) === -1) {
                customersSet.push(item.CustomerName);
            }
        return customersSet;
    }

    const multiplyArray = (arr, num) => {
        let result = [];
        for (let i = 0; i < num; i++)
            result = [...result, ...arr];
        return result;
    }

    const getExcelDataForCustomer = (customerName, hotelName) => {
        let excelSums = [];
        let reservations = data.filter(item => item.CustomerName === customerName && item.HotelName === hotelName);
        excelSums[0] = reservations.length;
        excelSums[1] = reservations.reduce((a, b) => a + b.TotalReservationBeforeTax, 0).toFixed(2)
        excelSums[2] = reservations.reduce((a, b) => a + b.TotalReservationAfterTax, 0).toFixed(2)
        return excelSums;
    }

    getHotelsList()

    const exportCustomExcel = () => {
        let wb = XLSX.utils.book_new();
        wb.SheetNames.push("Report");

        let excelAoa = [];
        let hotelsList = getHotelsList();
        let customersList = getCustomersList();
        excelAoa.push([{ v: `Custom Report ${fromDate?.toLocaleDateString()} - ${toDate?.toLocaleDateString()}`, t: "s", s: { font: { bold: true } } }])
        excelAoa.push(["Customer Name", ...getHotelsList(true), text("Total")].map(item => ({ v: item, t: "s", s: { fill: { fgColor: { rgb: "00b0f0" } }, alignment: { vertical: "center", horizontal: "center" }, font: { sz: 17, bold: true, color: { rgb: "ffffff" } } } })));
        // these title rows will have height of 3 rows so here is spacing for that
        excelAoa.push([])
        excelAoa.push([]);
        excelAoa.push([]);
        excelAoa.push(["", ...multiplyArray(["Reservations Count", "Total Sales Not Inc. Tax", "Total Sales Inc. Tax"], hotelsList.length + 1)]);
        customersList.forEach(customer => {
            let row = [customer];
            let totals = [0, 0, 0];
            hotelsList.forEach(hotel => {
                let hotelData = getExcelDataForCustomer(customer, hotel);
                row = [...row, ...hotelData];
                totals[0] += parseFloat(hotelData[0]);
                totals[1] += parseFloat(hotelData[1]);
                totals[2] += parseFloat(hotelData[2]);
            });
            row = [...row, ...totals.map(item => item.toFixed(2))];
            excelAoa.push(row);
        })

        wb.Sheets["Report"] = XLSX.utils.aoa_to_sheet(excelAoa);
        // +1 for the total at the end
        const merge = [...new Array(hotelsList.length + 1)].map((hotel, index) => ({ s: { r: 1, c: + index * 3 + 1 }, e: { r: 4, c: index * 3 + 3 } }));
        merge.push({ s: { r: 1, c: 0 }, e: { r: 5, c: 0 } });
        merge.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 15 } });
        wb.Sheets["Report"]["!merges"] = merge;

        XLSX.writeFile(wb, "CustomExcel.xlsx")
    }

    const CustomExcelButton = () => (
        <div>
            <Popup content='Custome Excel' position='bottom center' trigger={
                <Icon name='table' size='large' link onClick={exportCustomExcel} />
            } />
        </div>
    );

    const getCellValueForExport = (row, name) => {
        switch (name) {
            case "CheckOutDate":
                return moment(row[name]).format(dateFormat);
            case "CheckInDate":
                return moment(row[name]).format(dateFormat);
            case "IsManualCommission":
                return row[name]? "V": "";
            default:
                return row[name]
        }
    }

    const options = {
        search: {
            title: `${text("CustomerIncomeReport")}`,
            fields: [
                <Form.Field key="DateType" name="DateType" label="Date Type" control={
                    ({ ...rest }) => <DropDown
                        data={dateTypes}
                        textField="text"
                        valueField="value"
                        {...rest}
                    />}
                />,
                <DatePicker
                    label={text("FromDate")}
                    name="fromDate"
                    key="fromDate"
                    onChange={setFromDate}
                    required
                />,
                <DatePicker
                    label={text("ToDate")}
                    name="toDate"
                    key="toDate"
                    onChange={setToDate}
                    required
                />,

                <Form.Field
                    key="HotelIds"
                    label={text("Hotel")}
                    control={HotelsDropDown}
                    onChange={(ids) => setHotelIds(ids)}
                    placeholder={text("All")}
                    multiple
                    clearable
                />,
                <Form.Field
                    key="CustomerIds"
                    label={text("Customers")}
                    control={CustomerDropDown}
                    placeholder={text("All")}
                    clearable
                    multiple
                />,
                <Form.Field
                    key="RoomTypeIds"
                    label={text("RoomType")}
                    control={RoomTypesDropDown}
                    placeholder={text("All")}
                    clearable
                    multiple
                    spec={{ HotelIds: hotelIds }}
                    disabled={hotelIds.length === 0}
                />
            ],
            initial: {
                HotelId: currentHotel?.Id,
            },
            api: Reports.CustomerCommission,
            method: "POST",
            validate: ({ fromDate, toDate }) => {
                if (!(fromDate && toDate))
                    return false;
                if (new Date(toDate).getTime() < new Date(fromDate).getTime()) {
                    const error = new Error(text("FromDateIsAfterToDate"));
                    error.status = 400
                    throw (error);
                }
                return true;
            }
        },
        grid: {
            columns: [
                {
                    name: "HotelName",
                    title: text("Hotel"),
                    formatter
                },
                {
                    name: "CustomerName",
                    title: text("Customer"),
                    formatter: customerFormatter
                },
                {
                    name: "ReservationNumber",
                    title: text("ReservationNumber"),
                    formatter: reservationFormatter

                },
                {
                    name: "ExternalChannelMasterId",
                    title: `${text("ExternalReservationMasterID")}`,
                    formatter
                },
                {
                    name: "CheckInDate",
                    title: text("CheckInDate"),
                    formatter: dateFormatter
                },
                {
                    name: "CheckOutDate",
                    title: text("CheckOutDate"),
                    formatter: dateFormatter
                },
                {
                    name: "GuestName",
                    title: text("GuestName"),
                    formatter
                },
                {
                    name: "CustomerCommission",
                    title: text("CustomerCommission"),
                    formatter: ({ value }) => value === 0 ? "" : value
                },
                {
                    name: "IsManualCommission",
                    title: text("ManualCommission"),
                    formatter: ({ value }) => value ? <Icon name="check"/>: ""
                },
                {
                    name: "TotalReservationBeforeTax",
                    title: `${text("Reservation")} ${text("Total")} ${text("Before")} ${text("Tax")}`,
                    formatter: currencyFormatter

                },
                {
                    name: "TotalReservationAfterTax",
                    title: `${text("Reservation")} ${text("Total")} ${text("After")} ${text("Tax")}`,
                    formatter: currencyFormatter
                },
                {
                    name: "RoomType",
                    title: text("RoomType"),
                    formatter
                }
            ],
            summary: true,
            totalSummaryItems: [
                { columnName: 'TotalReservationBeforeTax', type: 'sum', formatter: currencyFormatter },
                { columnName: 'TotalReservationAfterTax', type: 'sum', formatter: currencyFormatter }
            ],
            cacheId: 'CustomerIncome',
            customOptions: {
                group: {
                    grouping: [
                        { columnName: 'HotelName'},
                        { columnName: 'CustomerName' },
                    ]
                },
                order: {},
                hidden: {
                    hiddenColumns:[
                        { columnName: "CustomerCommission" },
                        { columnName: "IsManualCommission" },
                    ],
                    columnsExtensions: hotelHasCommissionFeature? []: [
                        { columnName: "CustomerCommission", togglingEnabled: false },
                        { columnName: "IsManualCommission", togglingEnabled: false },
                    ],
                    toggleHidden: true,
                }
            },
            groupSummaryItems: [
                { columnName: 'TotalReservationBeforeTax', type: 'sum', showInGroupFooter: false, alignByColumn: true, formatter: currencyFormatter },
                { columnName: 'TotalReservationAfterTax', type: 'sum', showInGroupFooter: false, alignByColumn: true, formatter: currencyFormatter }
            ],
            excel: {
                customizeHeader: createExcelHeader(excelHeaderTitle),
                getCellValue: getCellValueForExport
            },
            menu: [
                <CustomExcelButton key="NewFile" />
            ],
            export: true,
            paging: true
        }
    }

    const onSearchComplete = async (newData) => {
        loader(true);
        try {
            let totals = await Promise.all(_.flatMap(_.groupBy(newData, "HotelId"), (reservations, hotelId) => {
                return _.chunk(reservations, 500).map(chunk => {
                    return GetReservationTotals({
                        HotelID: hotelId,
                        ReservationIDs: chunk.map(item => item.ReservationId),
                        TargetCurrencyId: window.user?.CurrentHotel?.MainCurrencyId || 1
                    })
                })
            }))
            totals = _.flatten(totals)
            newData.forEach(res => {
                const resTotals = totals.find(s => s.ReservationID == res.ReservationId)
                res.TotalReservationBeforeTax = resTotals?.TotalAmountDebitBeforeTax
                res.TotalReservationAfterTax = resTotals?.TotalAmountDebitAfterTax
            });
            setData(newData);
        }
        catch (error) {
            onError(error)
        }
        finally {
            loader(false);
        }
    }

    return <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", flex: 1}}>
        <GridSearchTemplate data={data} options={options} onSearchComplete={onSearchComplete} />
    </div>
}
